import { StoredTextureLike } from '@utils/sceneObjects/linked/textureCatalogApi'

export type UnitAcabadoTextureRecord = {
  descripcion?: string
  zona?: string
  modelo?: string
  acabado?: string
  idAcabadoUnidad?: number
  texturas?: {
    identificadorTextura?: number
    idTexturas?: number
    idGruposDeTexturas?: number
    nombre?: string
    url?: string
    color?: string
    factorRepeticionX?: number
    factorRepeticionY?: number
    opacidad?: number
    brillo?: number
    metal?: number
    reflejo?: number
    rugosidad?: number
    espejo?: boolean
  }
}

export function isUserAppliedLinkedTexture(texture: StoredTextureLike): boolean {
  const searchID = texture.searchID
  if (typeof searchID !== 'number' || searchID <= 0) return false
  return Boolean(texture.url || texture.name)
}

export function resolveBackendLinkedTextureId(
  acabado: UnitAcabadoTextureRecord,
): number | undefined {
  const id = acabado.texturas?.identificadorTextura ?? acabado.idAcabadoUnidad
  return typeof id === 'number' && id > 0 ? id : undefined
}

function buildTextureFromBackendDefaults(
  acabado: UnitAcabadoTextureRecord,
  zoneName: string,
  displayName: string,
): StoredTextureLike {
  const tex = acabado.texturas
  return {
    id: tex?.identificadorTextura ?? acabado.idAcabadoUnidad,
    name: tex?.nombre ?? '',
    zoneName,
    zoneDisplayName: displayName,
    zone: acabado.zona || displayName,
    url: tex?.url ?? '',
    groupID: tex?.idGruposDeTexturas ?? 0,
    searchID: tex?.idTexturas ?? 0,
    linkedTextureID: resolveBackendLinkedTextureId(acabado),
    color: tex?.color ?? '#FFFFFF',
    repeatX: tex?.factorRepeticionX ?? 1,
    repeatY: tex?.factorRepeticionY ?? 1,
    opacity: tex?.opacidad ?? 1,
    roughness: tex?.brillo ?? 1,
    metalness: tex?.metal ?? 0,
    envMapIntensity: tex?.reflejo ?? 0,
    bumpScale: tex?.rugosidad ?? 0,
    mirror: tex?.espejo ?? false,
    modelo: acabado.modelo,
    acabado: acabado.acabado,
  }
}

/**
 * Tras cambio de unidad (Gola, LI/LA/RU/R2…):
 * - Actualiza linkedTextureID / zone keys desde el backend (nuevo slot de zona).
 * - Preserva searchID, url, name… si el usuario ya aplicó una textura concreta.
 * - Zona Gola siempre toma defaults del backend.
 */
export function reconcileExistingTextureWithUnitAcabado(
  existingTexture: StoredTextureLike,
  acabado: UnitAcabadoTextureRecord,
): StoredTextureLike {
  const displayName = acabado.descripcion || acabado.zona || ''
  const zoneName = acabado.zona || existingTexture.zoneName || ''
  const isGolaZone = displayName === 'Gola' || zoneName === 'Gola'
  const backendLinkedId = resolveBackendLinkedTextureId(acabado)

  if (isGolaZone || !isUserAppliedLinkedTexture(existingTexture)) {
    const fromBackend = buildTextureFromBackendDefaults(acabado, zoneName, displayName)
    return {
      ...fromBackend,
      zoneName: zoneName || existingTexture.zoneName,
      zoneDisplayName: displayName || existingTexture.zoneDisplayName,
    }
  }

  const preservedName = existingTexture.name || acabado.texturas?.nombre || ''
  const preservedDescription =
    existingTexture.description || existingTexture.descripcion || preservedName

  return {
    ...existingTexture,
    linkedTextureID: backendLinkedId ?? existingTexture.linkedTextureID,
    id: acabado.texturas?.identificadorTextura ?? existingTexture.id,
    zoneName: zoneName || existingTexture.zoneName,
    zoneDisplayName: displayName || existingTexture.zoneDisplayName,
    zone: displayName || existingTexture.zone || existingTexture.zoneDisplayName,
    name: preservedName,
    description: preservedDescription,
    descripcion: preservedDescription,
    acabado: existingTexture.acabado ?? acabado.acabado,
    modelo: existingTexture.modelo ?? acabado.modelo,
  }
}

/**
 * Fusiona textura del GLB (slots/zona) con la aplicada por el usuario (identidad visual).
 */
export function mergeGlbZoneTextureWithUserApplied(
  glbTexture: StoredTextureLike,
  userTexture: StoredTextureLike,
): StoredTextureLike {
  const zoneName = glbTexture.zoneName || userTexture.zoneName
  const zoneDisplayName = glbTexture.zoneDisplayName || userTexture.zoneDisplayName
  const zone = zoneDisplayName || glbTexture.zone || userTexture.zone

  if (!isUserAppliedLinkedTexture(userTexture)) {
    return {
      ...glbTexture,
      zoneName,
      zoneDisplayName,
      zone,
    }
  }

  return {
    ...userTexture,
    id: glbTexture.id || userTexture.id,
    linkedTextureID: glbTexture.linkedTextureID || userTexture.linkedTextureID,
    zoneName,
    zoneDisplayName,
    zone,
  }
}
